import type { AssetRopaReview, AssetRopaWarningFlags } from 'config/api/ropaReviews/ropaReviews.types';

import type {
  AssetRopaBooleanFilterValue,
  AssetRopaDpiaFilterState,
  AssetRopaOwnerDpiaFilterState,
  AssetRopaReviewFilters,
} from './assetRopaFilter.types';
import { mapBooleanFilterValue } from './assetRopaFilterMappers';

export const matchesBooleanWarningFilter = (flag: boolean, filterValue: AssetRopaBooleanFilterValue): boolean => {
  const expected = mapBooleanFilterValue(filterValue);
  if (expected === undefined) {
    return true;
  }

  return flag === expected;
};

export const matchesDpiaFilterState = (
  warnings: AssetRopaWarningFlags,
  dpiaState: AssetRopaDpiaFilterState | AssetRopaOwnerDpiaFilterState,
): boolean => {
  if (!dpiaState) {
    return true;
  }

  if (dpiaState === 'indicated') {
    return warnings.dpiaIndicated;
  }

  if (dpiaState === 'required_unapproved') {
    return warnings.dpiaRequiredUnapproved;
  }

  return !warnings.dpiaIndicated && !warnings.dpiaRequiredUnapproved;
};

export const matchesWarningStateFilter = (
  warnings: AssetRopaWarningFlags,
  warningState: AssetRopaReviewFilters['warningState'],
  hasWarnings: (value: AssetRopaWarningFlags) => boolean,
): boolean => {
  if (!warningState) {
    return true;
  }

  const assetHasWarnings = hasWarnings(warnings);
  if (warningState === 'with_warnings') {
    return assetHasWarnings;
  }

  return !assetHasWarnings;
};

export const matchesAssetRopaReviewFilters = (
  asset: AssetRopaReview,
  filters: AssetRopaReviewFilters,
  hasWarnings: (warnings: AssetRopaWarningFlags) => boolean,
): boolean => {
  const normalizedSearch = filters.search.trim().toLowerCase();
  const matchesSearch =
    !normalizedSearch ||
    asset.assetName.toLowerCase().includes(normalizedSearch) ||
    (asset.operationalOwnerName?.toLowerCase().includes(normalizedSearch) ?? false);

  const matchesReviewStatus = !filters.reviewStatus || (asset.hasReviewOverlay !== false && asset.reviewStatus === filters.reviewStatus);

  return (
    matchesSearch &&
    matchesReviewStatus &&
    (!filters.assetTypeId || asset.assetTypeId === filters.assetTypeId) &&
    (!filters.operationalOwnerMembershipId || asset.operationalOwnerMembershipId === filters.operationalOwnerMembershipId) &&
    matchesWarningStateFilter(asset.warnings, filters.warningState, hasWarnings) &&
    matchesDpiaFilterState(asset.warnings, filters.dpiaState) &&
    matchesBooleanWarningFilter(asset.warnings.sensitivePersonalData, filters.sensitivePersonalData) &&
    matchesBooleanWarningFilter(asset.warnings.thirdCountryProcessing, filters.thirdCountryProcessing)
  );
};
